import { clientEnv } from '@/shared/config/env';
import {
  connectSiteRealtime,
  type SiteRealtimeFrame,
  type SiteRealtimeHandle,
  type SiteRealtimeScene,
} from './site-realtime';

type FrameListener = (frame: SiteRealtimeFrame) => void;

/** 每个主题保留的事件帧上限：只用于给后挂载的组件回放，跑马灯本身也只留最近若干条。 */
const REPLAY_EVENT_LIMIT = 50;

/**
 * 全站共用一条站点 WS：主题订阅（跑马灯等）与登录玩家在线态都走这里，避免同一页面开多条连接。
 * 有订阅或已登录时才连接；凭证变化（登录 / 退出 / 过期）重新握手，场景变化只发一帧。
 * 服务端对已订阅主题不重发快照、也没有退订，所以按主题缓存最近快照 + 之后的事件，
 * 组件重新挂载时先回放，效果等同于新连接收到的快照。
 */
const listeners = new Map<string, Set<FrameListener>>();
const replay = new Map<string, { events: SiteRealtimeFrame[]; snapshot: SiteRealtimeFrame | null }>();
let subscribedTopics = new Set<string>();
let token = '';
let scene: SiteRealtimeScene | null = null;
let handle: SiteRealtimeHandle | null = null;

function remember(frame: SiteRealtimeFrame) {
  if (!frame.topic) return;
  if (frame.op === 'snapshot') {
    replay.set(frame.topic, { events: [], snapshot: frame });
    return;
  }
  if (frame.op === 'event') {
    const entry = replay.get(frame.topic) ?? { events: [], snapshot: null };
    entry.events = [...entry.events, frame].slice(-REPLAY_EVENT_LIMIT);
    replay.set(frame.topic, entry);
  }
}

function dispatch(frame: SiteRealtimeFrame) {
  remember(frame);
  if (frame.topic) {
    for (const listener of listeners.get(frame.topic) ?? []) listener(frame);
    return;
  }
  for (const group of listeners.values()) {
    for (const listener of group) listener(frame);
  }
}

function reconnect() {
  handle?.stop();
  handle = null;
  replay.clear();
  subscribedTopics = new Set(listeners.keys());
  if (!token && listeners.size === 0) return;
  handle = connectSiteRealtime({
    onFrame: dispatch,
    siteId: clientEnv.siteId,
    token,
    topics: [...subscribedTopics],
  });
  handle.setScene(scene);
}

export function subscribeSiteRealtime(topic: string, listener: FrameListener): () => void {
  let group = listeners.get(topic);
  if (!group) {
    group = new Set();
    listeners.set(topic, group);
  }
  group.add(listener);
  if (!handle) {
    reconnect();
  } else if (subscribedTopics.has(topic)) {
    const cached = replay.get(topic);
    if (cached?.snapshot) listener(cached.snapshot);
    for (const event of cached?.events ?? []) listener(event);
  } else {
    subscribedTopics.add(topic);
    handle.subscribe([topic]);
  }

  return () => {
    const current = listeners.get(topic);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(topic);
    if (!token && listeners.size === 0) {
      handle?.stop();
      handle = null;
      replay.clear();
      subscribedTopics = new Set();
    }
  };
}

export function setSiteRealtimeToken(next: string) {
  const value = next.trim();
  if (value === token) return;
  token = value;
  reconnect();
}

export function setSiteRealtimeScene(next: SiteRealtimeScene) {
  if (scene?.scene === next.scene && scene?.gameCode === next.gameCode) return;
  scene = next;
  handle?.setScene(next);
}

/** 仅测试用：清空模块状态。 */
export function resetSiteRealtimeHub() {
  handle?.stop();
  handle = null;
  token = '';
  scene = null;
  listeners.clear();
  replay.clear();
  subscribedTopics = new Set();
}
